import { useEffect, useRef } from 'react'
import Markdown from 'react-markdown'
import AskUserMessage from './AskUserMessage'

interface AskUserQuestion {
  question: string
  input_type: 'text' | 'buttons' | 'dropdown'
  options?: string[]
}

interface Message {
  id: string
  role: string
  content: string
  created_at: string
  message_type?: string
  metadata?: AskUserQuestion
  answered?: string
}

interface Props {
  messages: Message[]
  streaming?: boolean
  onAnswer?: (answer: string) => void
}

function AgentAvatar() {
  return (
    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-600/20 mt-0.5">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2">
        <path d="M12 2a4 4 0 0 0-4 4v2H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2h-2V6a4 4 0 0 0-4-4z"/>
      </svg>
    </div>
  )
}

function isErrorMessage(content: string) {
  return content.startsWith('Error:')
}

export default function ChatPanel({ messages, streaming, onAnswer }: Props) {
  const bottomRef = useRef<HTMLDivElement>(null)
  const lastMessage = messages[messages.length - 1]
  const hasStreamingContent = streaming && lastMessage?.id === 'streaming' && lastMessage.content.length > 0

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, streaming])

  return (
    <div className="flex-1 overflow-y-auto p-5 space-y-5">
      {messages.map(msg => (
        <div key={msg.id} className="flex gap-3">
          {msg.role === 'user' ? (
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-200 text-[11px] font-medium text-gray-700 mt-0.5">
              U
            </div>
          ) : (
            <AgentAvatar />
          )}
          <div className="flex-1 min-w-0">
            <div className="mb-1 flex items-center gap-2">
              <span className={`text-sm font-medium ${msg.role === 'user' ? 'text-gray-900' : 'text-blue-600'}`}>
                {msg.role === 'user' ? 'You' : 'Agent'}
              </span>
              <span className="text-[11px] text-gray-400">
                {new Date(msg.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
              </span>
            </div>
            {msg.message_type === 'ask_user' && msg.metadata ? (
              <AskUserMessage
                question={msg.metadata}
                answered={msg.answered}
                onAnswer={onAnswer || (() => {})}
              />
            ) : isErrorMessage(msg.content) ? (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                {msg.content}
              </div>
            ) : msg.role === 'assistant' ? (
              <div className="prose prose-sm prose-gray max-w-none text-gray-700 prose-headings:text-gray-900 prose-headings:text-sm prose-headings:font-semibold prose-strong:text-gray-900 prose-p:leading-relaxed prose-li:text-gray-700 prose-ul:my-1 prose-ol:my-1">
                <Markdown>{msg.content}</Markdown>
              </div>
            ) : (
              <div className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
                {msg.content}
              </div>
            )}
          </div>
        </div>
      ))}

      {streaming && !hasStreamingContent && (
        <div className="flex gap-3">
          <AgentAvatar />
          <div className="flex items-center gap-1 pt-2">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500" style={{ animationDelay: '0ms' }} />
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500" style={{ animationDelay: '200ms' }} />
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500" style={{ animationDelay: '400ms' }} />
          </div>
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  )
}
